import { Injectable } from '@angular/core';
import { BehaviorSubject, delay, map, Observable, of, switchMap } from 'rxjs';
import { DateUtil } from '../lib/date-util';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { StringUtil } from '../lib/string-util';
import { DataService } from './data.service';
import { IDataServiceEntityFileEntryList, IDataServiceEntity } from './data.service.interface';
import { NumberUtil } from '../lib/number-util';
import { ImageService } from './image.service';

@Injectable({
  providedIn: 'root'
})
export class PropertyService {

  private EntityKey: string = 'property';

  /**
   * @param dataService
   */
  constructor(private dataService: DataService, private authService: AuthService, private imageService: ImageService) {}

  /**
   * 
   * @param property 
   * @returns 
   */
  imageUrlPrimary(property: IPropertyEntity): string {
    const ret = property.propertyInfo.photoFileEntryList.fileEntries.find(fe => fe.code == property.propertyInfo.photoFileEntryList.primaryImageCode)?.fileUrl;
    return ret;
  }

  /**
   * 
   * @returns 
   */
  imageCodeNew(): string {
    return StringUtil.newId();
  }

  /**
   * 
   * @returns 
   */
  loadSamples(): Observable<IPropertyEntity[]> {
    if (!environment.sampleProperties)
      return of([]);

    return this.dataService.saveMany(this.EntityKey, this.propertySamples()).pipe(
      switchMap(() => this.dataService.loadMany<IPropertyEntity>(this.EntityKey, undefined))
    );    
  }

  /**
     *     
     * @returns 
     */
  propertyList(): Observable<IPropertyEntity[]> {
    return this.dataService.loadMany<IPropertyEntity>(this.EntityKey).pipe(
      // switchMap(properties => {
      //   if (properties.length == 0 && environment.sampleProperties)
      //     return this.dataService.saveMany(this.EntityKey, this.propertySamples()).pipe(
      //       switchMap(() => this.dataService.loadMany<IPropertyEntity>(this.EntityKey, undefined))
      //     );
      //   else
      //     return of(properties);
      // })
    );    
  }

  /**
   * 
   * @param code 
   */
  propertyFind(code: string): Observable<IPropertyEntity> {
    return this.dataService.loadOne(this.EntityKey, code);
  }

  /**
   * 
   * @returns 
   */
  propertyNewData(): IPropertyNewData {
    const ret: IPropertyNewData = {
      code: undefined,
      name: 'new property',
      description: '',
      address: '',

      purchaseDate: DateUtil.isoDateString(DateUtil.now()),
      purchasePrice: 0.0,
      marketValue: 0.0,

      monthlyRevenue: 0.0,
      monthlyExpenses: {
        hoa: 0.0, grounds: 0.0, other: 0.0, utilities: 0.0
      },
      yearlyExpenses: {
        taxes: 0.0, insurance: 0.0
      },

      propertyManagement: this.defaultInvestmentVariables().propertyManagement,

      hasLoan: true,
      loanParams: this.defaultPropertyLoanParams()
    };
    return ret;
  }

  /**
   * 
   * @param data 
   * @returns 
   */
  propertyCopy(data: IPropertyEntity): Observable<IPropertyEntity> {
    const copy: IPropertyEntity = JSON.parse(JSON.stringify(data));
    copy.code = undefined;
    copy.propertyInfo.name = `${copy.propertyInfo.name} *copy*`;

    const fileStorageEntries = (data.propertyInfo.photoFileEntryList.fileEntries || []).filter(fe => (fe.fileUrl || '').toUpperCase().startsWith('STORAGE:'));
    if (fileStorageEntries.length > 1) {
      throw `Only 1 property image is supported at this time`;
    } else if (fileStorageEntries.length == 1) {
      return this.imageService.loadOne(fileStorageEntries[0].code).pipe(
        map(fileData => {
          copy.propertyInfo.photoFileEntryList.fileEntries[0].code = this.imageCodeNew();
          copy.propertyInfo.photoFileEntryList.primaryImageCode = copy.propertyInfo.photoFileEntryList.fileEntries[0].code;
          copy.propertyInfo.photoFileEntryList.fileEntries[0].fileUrl = fileData;
        }),
        switchMap(() => this.propertySave(copy))
      );
    } else {
      return this.propertySave(copy);      
    }
  }

  /**
   * 
   * @param data 
   * @returns 
   */
  propertySave(data: IPropertyEntity): Observable<IPropertyEntity> {
    const fileDataEntries = (data.propertyInfo.photoFileEntryList.fileEntries || []).filter(fe => (fe.fileUrl || '').toUpperCase().startsWith('DATA:'));

    if (fileDataEntries.length > 1) {
      throw `Only 1 property image is supported at this time`;
    } else if (fileDataEntries.length == 1) {
      return this.imageService.save(fileDataEntries[0].code, fileDataEntries[0].fileUrl).pipe(
        map(fileData => fileDataEntries[0].fileUrl = `STORAGE:${fileDataEntries[0].code}`),
        switchMap(() => this.dataService.saveOne(this.EntityKey, data))
      );
    } else {
      return this.dataService.saveOne(this.EntityKey, data);
    }    
  }

  /**
   * 
   * @param code 
   * @param propertyInfo 
   * @param loanParams 
   * @param investmentVariables 
   */
  propertyUpdate(code: string, propertyInfo: IPropertyInfo, loanParams: ILoanParams, investmentVariables: IInvestmentVariables, inactive: boolean): Observable<IPropertyEntity> {
    const entity = this.propertyEntity(code, propertyInfo, loanParams, investmentVariables, inactive);      
    return this.propertySave(entity);
  }

  /**
   * 
   * @param code 
   * @returns 
   */
  propertyDelete(code: string): Observable<IPropertyEntity> {
    return this.dataService.deleteOne(this.EntityKey, code);
  }

  /**
   * 
   * @param code 
   * @param propertyInfo 
   * @param loanParams 
   * @param investmentVariables 
   */
  propertyCreate(data: IPropertyNewData): Observable<IPropertyEntity> {

    const propertyInfo: IPropertyInfo = {
      name: data.name, description: data.description, address: data.address,

      purchaseDate: data.purchaseDate, purchasePrice: data.purchasePrice, purchaseCosts: 0.0,
      initialValues: {
        yearlyExpenses: {
          ...data.yearlyExpenses
        },
        marketValue: data.purchasePrice,
        monthlyRevenue: data.monthlyRevenue,
        monthlyExpenses: {
          ...data.monthlyExpenses
        }
      },
      photoFileEntryList: { fileEntries: [], primaryImageCode: undefined }
    };
    
    const defaultVariables: IInvestmentVariables = {
      ...this.defaultInvestmentVariables(),
      propertyManagement: data.propertyManagement
    };

    const entity = this.propertyEntity(data.code, propertyInfo, data.hasLoan ? data.loanParams : undefined, this.defaultInvestmentVariables(), false);
    return this.propertySave(entity);
  }


  /**
  * 
  * @returns 
  */
  private propertySamples(): IPropertyEntity[] {
    const samples: IPropertyEntity[] = environment.sampleProperties.map(sp => {
      const code: string = sp.code;

      const propertyInfo: IPropertyInfo = sp.propertyInfo;
      propertyInfo.purchaseDate = DateUtil.isoDateString(DateUtil.now());

      const loanBase: ILoanParams = sp.loanParams;
      const investmentVariables: IInvestmentVariables = sp.investmentVariables;

      const ret: IPropertyEntity = this.propertyEntity(code, propertyInfo, loanBase, investmentVariables);
      return ret;
    });
    return samples;
  }

  /**
   * 
   * @param code
   * @param propertyInfo 
   * @param loanParams 
   * @param investmentVariables
   * @param inactive
   * @returns 
   */
  private propertyEntity(code: string, propertyInfo: IPropertyInfo, loanParams: ILoanParams, investmentVariables: IInvestmentVariables, inactive: boolean = false): IPropertyEntity {
    // calculate the loan, will only be done 1 time at the creation of the property entity
    const loan: ILoan = !loanParams ? undefined : this.propertyLoan(propertyInfo.purchaseDate, propertyInfo.purchasePrice, loanParams);

    const initialInvestment = (propertyInfo.purchaseCosts ?? 0) + (loan?.downpayment ?? propertyInfo.purchasePrice ?? 0.0) + (loan?.loanParams?.closingCosts ?? 0);
    const years = this.investmentYears(propertyInfo, initialInvestment, loan, investmentVariables);

    const ret: IPropertyEntity = {
      code: code,
      propertyInfo: propertyInfo,
      initialInvestment: initialInvestment,
      loan: loan,

      investmentVariables: investmentVariables,
      investmentYears: years,

      inactive: inactive
    };
    return ret;
  }

  //#region defaults

  /**
   * 
   * @returns 
   */
  private defaultInvestmentVariables(): IInvestmentVariables {
    const ret: IInvestmentVariables = {
      propertyAppreciation: 4.0,
      rentalAppreciation: 3.0,
      propertyManagement: 8.0,
      maintenance: 3.0,
      vacancy: 3.0,
      taxBracket: 30.0,
      saleCommission: 6.0,
      saleClosingCosts: .5,
      expenseInflation: 2.0,
      depreciationYears: 27.5
    };
    return ret;
  }

  /**
   * 
   * @returns 
   */
  defaultPropertyInfo(): IPropertyInfo {
    const ret: IPropertyInfo = {
      name: 'New Property',

      initialValues: {
        yearlyExpenses: {
          taxes: 0.0, insurance: 0.0
        },
        monthlyExpenses: {
          hoa: 0.0, grounds: 0.0, other: 0.0, utilities: 0.0 
        }
      },

      photoFileEntryList: {
        fileEntries: []
      }
    };
    return ret;
  }

  /**
   * 
   * @returns 
   */
  defaultPropertyLoanParams(): ILoanParams {
    const ret: ILoanParams = {
      percentDown: 20.0, years: 30, rate: 6.0, closingCosts: 0
    };
    return ret;
  }
  //#endregion defaults

  /**
   * 
   * @param startDate 
   * @param price
   * @param loanParams
   * @returns 
   */
  private propertyLoan(startDate: string, price: number, loanParams: ILoanParams): ILoan {
    const interestRate = NumberUtil.percent(loanParams.rate);
    
    const loanMonths = loanParams.years * 12;
    const loanMonthlyRate = (interestRate) / 12;
    const downpayment = NumberUtil.percent(loanParams.percentDown) * price;

    const amount = price - downpayment;

    const ret: ILoan = {
      loanParams: loanParams,

      startDate: DateUtil.Parse(startDate),

      months: loanParams.years * 12,

      price: price,
      downpayment: downpayment,

      amount: amount,

      monthlyPayment: (loanMonthlyRate / (1 - (Math.pow((1 + loanMonthlyRate), -(loanMonths))))) * amount,
      monthlyRate: loanMonthlyRate,

      payments: []
    };

    this.propertyLoanCalculatePayments(ret);

    return ret;
  }

  /**
   * 
   * @param loan 
   */
  private propertyLoanCalculatePayments(loan: ILoan) {
    const payments: ILoanPayment[] = [];

    for (let i = 0; i <= loan.months; i++) {
      const monthNumber = i + 1;
      const yearNumber = Math.floor(i / 12) + 1;

      const lastPayment: ILoanPayment = payments.length == 0 ? undefined : payments[payments.length - 1];
      const amountRemaining = lastPayment?.amountRemaining ?? loan.amount;

      const interestForMonth = amountRemaining * loan.monthlyRate;
      const principalForMonth = loan.monthlyPayment - interestForMonth;

      const item = {
        paymentDate: DateUtil.AddMonths(new Date(loan.startDate), monthNumber),
        monthNumber: monthNumber, yearNumber: yearNumber,
        principal: principalForMonth, interest: interestForMonth,
        amountRemaining: amountRemaining - principalForMonth,
        interestToDate: (lastPayment?.interestToDate ?? 0) + interestForMonth,
        principalToDate: (lastPayment?.principalToDate ?? 0) + principalForMonth
      };
      payments.push(item);
    }

    loan.payments = payments;
  }

  /**
   * 
   * @param propertyInfo 
   * @param initialInvestment 
   * @param loan 
   * @param investmentVariables 
   * @param options 
   * @param recalcOptions 
   * @returns 
   */
  private investmentYears(
    propertyInfo: IPropertyInfo, initialInvestment: number, loan: ILoan, investmentVariables: IInvestmentVariables,
    options: { maxYears?: number } = undefined,
    recalcOptions: { startYear: number, currentYears: IInvestmentYear[] } = undefined
  ): IInvestmentYear[] {

    const maxYears: number = options?.maxYears ?? 35;
    const startingYear: number = recalcOptions?.startYear ?? 1;
    const years: IInvestmentYear[] = recalcOptions?.currentYears ? [...recalcOptions?.currentYears] : [];

    // calculate each year
    for (let y: number = startingYear; y <= maxYears; y++) {
      //const payment = loan.payments.find(p => p.yearNumber == y && p.monthNumber == 12);
      const paymentsForYear = loan?.payments?.filter(p => p.yearNumber == y) ?? [];
      const principalForYear = paymentsForYear.reduce((pv, cv) => pv + cv.principal, 0.0);
      const interestForYear = paymentsForYear.reduce((pv, cv) => pv + cv.interest, 0.0);

      // use for year over year calcs
      const previousYear: IInvestmentYear = y == 1 ? undefined : years.find(iy => iy.year == y - 1);

      // build the same data structure as the Excel details sheet
      {

        // gross scheduled income
        const gsiComponents: IGrossScheduledIncomeComponents = {
          rental: y == 1 ? propertyInfo.initialValues.monthlyRevenue * 12
            : previousYear.grossScheduledIncome.estimated.rental + (previousYear.grossScheduledIncome.estimated.rental * NumberUtil.percent(investmentVariables.rentalAppreciation)),
          other: []
        }
        const grossScheduledIncome: IGrossScheduledIncome = {
          ...gsiComponents,
          total: gsiComponents.rental + gsiComponents.other.reduce((pv, cv) => pv + cv.amount, 0.0)
        }

        // gross operating income
        const goiComponents: IGrossOperatingIncomeComponents = {
          vacancy: grossScheduledIncome.rental * NumberUtil.percent(investmentVariables.vacancy),
          rental: grossScheduledIncome.rental - (grossScheduledIncome.rental * NumberUtil.percent(investmentVariables.vacancy)),
          other: [
            ...grossScheduledIncome.other
          ]
        };
        const grossOperatingIncome: IGrossOperatingIncome = {
          ...goiComponents,
          total: goiComponents.rental + goiComponents.other.reduce((pv, cv) => pv + cv.amount, 0.0)
        }

        // fixed expenses
        const expensesFixedComponents: IExpensesFixedComponents = {
          taxes: y == 1 ? propertyInfo.initialValues.yearlyExpenses?.taxes ?? 0.0
            : previousYear.operatingExpenses.estimated.fixed.taxes + (previousYear.operatingExpenses.estimated.fixed.taxes * NumberUtil.percent(investmentVariables.expenseInflation)),
          insurance: y == 1 ? propertyInfo.initialValues.yearlyExpenses?.insurance ?? 0.0
            : previousYear.operatingExpenses.estimated.fixed.insurance + (previousYear.operatingExpenses.estimated.fixed.insurance * NumberUtil.percent(investmentVariables.expenseInflation)),

          // 7/15/23 jmo, changed to use GSI instead of GOI as per Zach... didn't want the PM costs to be based on gross rents minus vacance
          propertyManagement: grossScheduledIncome.rental * NumberUtil.percent(investmentVariables.propertyManagement),

          hoa: y == 1 ? (propertyInfo.initialValues.monthlyExpenses?.hoa ?? 0.0) * 12
            : previousYear.operatingExpenses.estimated.fixed.hoa + (previousYear.operatingExpenses.estimated.fixed.hoa * NumberUtil.percent(investmentVariables.expenseInflation)),
          grounds: y == 1 ? (propertyInfo.initialValues.monthlyExpenses?.grounds ?? 0.0) * 12
            : previousYear.operatingExpenses.estimated.fixed.grounds + (previousYear.operatingExpenses.estimated.fixed.grounds * NumberUtil.percent(investmentVariables.expenseInflation)),
          utilities: y == 1 ? (propertyInfo.initialValues.monthlyExpenses?.utilities ?? 0.0) * 12
            : previousYear.operatingExpenses.estimated.fixed.utilities + (previousYear.operatingExpenses.estimated.fixed.utilities * NumberUtil.percent(investmentVariables.expenseInflation)),
          other: y == 1 ? (propertyInfo.initialValues.monthlyExpenses?.other ?? 0.0) * 12
            : previousYear.operatingExpenses.estimated.fixed.other + (previousYear.operatingExpenses.estimated.fixed.other * NumberUtil.percent(investmentVariables.expenseInflation))
        };
        const expensesFixed: IExpensesFixed = {
          ...expensesFixedComponents,
          total: expensesFixedComponents.taxes + expensesFixedComponents.insurance + expensesFixedComponents.hoa + expensesFixedComponents.grounds
            + expensesFixedComponents.utilities + expensesFixedComponents.propertyManagement
            + expensesFixedComponents.other
        };

        // variable expenses
        const expensesVariableComponents: IExpensesVariableComponents = {
          maintenance: grossOperatingIncome.total * NumberUtil.percent(investmentVariables.maintenance),
          other: []
        };
        const expensesVariable: IExpensesVariable = {
          ...expensesVariableComponents,
          total: expensesVariableComponents.maintenance
            + expensesVariableComponents.other.reduce((pv, cv) => pv + cv.amount, 0.0)
        };

        // operating expenses
        const operatingExpenses: IOperatingExpenses = {
          fixed: expensesFixed,
          variable: expensesVariable,
          total: expensesFixed.total + expensesVariable.total
        };

        // net operating income
        const netOperatingIncome = grossOperatingIncome.total - operatingExpenses.total;

        // debt service
        const debtService = loan ? (y <= loan?.loanParams?.years ? loan.monthlyPayment * 12 : 0.0) : 0.0;

        // end of year totals
        const dbComponents = {
          purchasePrice: propertyInfo.purchasePrice,
          landValue: 0,
          purchaseCosts: (propertyInfo.purchaseCosts ?? 0.0) + (loan?.loanParams?.closingCosts ?? 0.0),
          rehabCosts: 0
        };
        const depreciationBase = {
          ...dbComponents,

          // todo.. check with zach on calculation and usage... 
          //   - ignore land value for now
          total: dbComponents.purchasePrice - dbComponents.landValue + (0 /* dbComponents.purchaseCosts */) + dbComponents.rehabCosts
        }
        const
          endOfYearAppreciation = y == 1 ? propertyInfo.initialValues.marketValue * NumberUtil.percent(investmentVariables.propertyAppreciation)
            : previousYear.endOfYearTotals.estimated.propertyValue * NumberUtil.percent(investmentVariables.propertyAppreciation),
          endOfYearCashflow = netOperatingIncome - debtService,
          endOfYearDepreciation = y <= (investmentVariables.depreciationYears ?? 27) ? (depreciationBase.total / (investmentVariables.depreciationYears ?? 27)) : 0.0,
          endOfYearDepreciationPassive = endOfYearDepreciation == 0 ? 0
            : endOfYearCashflow <= 0 ? 0
              : endOfYearCashflow >= endOfYearDepreciation ? endOfYearDepreciation
                : endOfYearCashflow,
          endOfYearDepreciationActive = endOfYearDepreciation == 0 ? 0 : endOfYearDepreciation - endOfYearDepreciationPassive,
          endOfYearEquityBase = y > 1 ? 0
            : (
              loan ? ((propertyInfo.initialValues.marketValue ?? (propertyInfo.purchasePrice ?? 0)) - (propertyInfo.purchasePrice ?? 0)) + (loan.downpayment ?? 0)
                : propertyInfo.initialValues.marketValue ?? (propertyInfo.purchasePrice ?? 0)
            ); 
        const endOfYearTotalsComponents: IEndOfYearTotalsComponents = {
          // user can override these at teh end of the year with actuals
          propertyValue: !previousYear ? propertyInfo.initialValues.marketValue + (propertyInfo.initialValues.marketValue * NumberUtil.percent(investmentVariables.propertyAppreciation))
            : previousYear.endOfYearTotals.estimated.propertyValue + (previousYear.endOfYearTotals.estimated.propertyValue * NumberUtil.percent(investmentVariables.propertyAppreciation)),
          capex: 0.0,
          cashFlow: endOfYearCashflow,

          depreciationBase: depreciationBase,          
          depreciation: {
            actual: endOfYearDepreciation,
            passive: endOfYearDepreciationPassive,
            passiveTaxImpact: endOfYearDepreciationPassive * NumberUtil.percent(investmentVariables.taxBracket),
            active: endOfYearDepreciationActive,
            activeTaxImpact: endOfYearDepreciationActive * NumberUtil.percent(investmentVariables.taxBracket)
          },

          appreciation: endOfYearAppreciation,
          tenantPaydown: principalForYear,
          equity: endOfYearEquityBase + endOfYearAppreciation + principalForYear
        };
        const endOfYearTotals: IEndOfYearTotals = {
          ...endOfYearTotalsComponents,
          total: endOfYearTotalsComponents.appreciation + endOfYearTotalsComponents.cashFlow + endOfYearTotalsComponents.tenantPaydown
        };

        // cash on cash
        const cashOnCashComponents: ICashOnCashComonents = {
          cashflow: endOfYearTotals.cashFlow / initialInvestment,
          appreciation: endOfYearTotals.appreciation / initialInvestment,
          tenantPaydown: endOfYearTotals.tenantPaydown / initialInvestment,
          
          depreciation: {
            passive: endOfYearTotals.depreciation.passiveTaxImpact / initialInvestment,
            active: endOfYearTotals.depreciation.activeTaxImpact / initialInvestment
          }
        };
        const cashOnCash: ICashOnCash = {
          ...cashOnCashComponents,
          total: cashOnCashComponents.cashflow + cashOnCashComponents.appreciation + cashOnCashComponents.tenantPaydown
        };

        // sale
        const salesCosts = {
          commission: endOfYearTotals.propertyValue * NumberUtil.percent(investmentVariables.saleCommission),
          closing: endOfYearTotals.propertyValue * NumberUtil.percent(investmentVariables.saleClosingCosts),
          other: 0.0
        };
        const saleComponents: ISaleComponents = {
          price: endOfYearTotals.propertyValue,
          costs: {
            ...salesCosts,
            total: salesCosts.closing + salesCosts.commission + salesCosts.other
          }
        };
        const sale: ISale = {
          ...saleComponents,
          net: saleComponents.price - saleComponents.costs.total
        };

        // running totals;
        const runningTotalsComponents = {
          cashflow: (previousYear?.runningTotals?.cashflow ?? 0.0) + endOfYearTotals.cashFlow,

          depreciation: {
            total: (previousYear?.runningTotals?.depreciation.total ?? 0.0) + endOfYearTotals.depreciation.actual,
            active: (previousYear?.runningTotals?.depreciation.active ?? 0.0) + endOfYearTotals.depreciation.active,
            activeTaxImpact: (previousYear?.runningTotals?.depreciation.activeTaxImpact ?? 0.0) + endOfYearTotals.depreciation.activeTaxImpact,
            passive: (previousYear?.runningTotals?.depreciation.passive ?? 0.0) + endOfYearTotals.depreciation.passive,
            passiveTaxImpact: (previousYear?.runningTotals?.depreciation.passiveTaxImpact ?? 0.0) + endOfYearTotals.depreciation.passiveTaxImpact,
          },

          appreciation: (previousYear?.runningTotals?.appreciation ?? 0.0) + endOfYearTotals.appreciation,
          tenantPaydown: (previousYear?.runningTotals?.tenantPaydown ?? 0.0) + endOfYearTotals.tenantPaydown,

          capex: (previousYear?.runningTotals?.capex ?? 0.0) + endOfYearTotals.capex,

          marketValue: (previousYear?.runningTotals?.marketValue ?? propertyInfo.initialValues.marketValue) + endOfYearTotals.appreciation,
          equity: (previousYear?.runningTotals?.equity ?? 0.0) + endOfYearTotals.equity,
        };
        const runningTotalsCashOnCashComponents: ICashOnCashComonents = {
          cashflow: runningTotalsComponents.cashflow / initialInvestment,
          appreciation: runningTotalsComponents.appreciation / initialInvestment,
          tenantPaydown: runningTotalsComponents.tenantPaydown / initialInvestment,

          depreciation: {
            passive: runningTotalsComponents.depreciation.passiveTaxImpact / initialInvestment,
            active: runningTotalsComponents.depreciation.activeTaxImpact / initialInvestment
          }
        };
        const
          saleProfit = runningTotalsComponents.cashflow + runningTotalsComponents.equity - sale.costs.total - initialInvestment;
        const runningTotals: IRunningTotals = {
          ...runningTotalsComponents,

          cashOnCash: {
            ...runningTotalsCashOnCashComponents,
            total: runningTotalsCashOnCashComponents.cashflow
              + runningTotalsCashOnCashComponents.appreciation
              + runningTotalsCashOnCashComponents.tenantPaydown
          },

          investmentReturn: saleProfit,
          roi: saleProfit / initialInvestment
        };

        // temp definition to be converted into the data inteface later, once it's worked out
        const investmentYear: IInvestmentYear = {
          year: y,
          calendarYear: DateUtil.Parse(propertyInfo.purchaseDate).getFullYear() + (y - 1),

          grossScheduledIncome: {
            estimated: grossScheduledIncome,
            actual: undefined
          },

          grossOperatingIncome: {
            estimated: grossOperatingIncome,
            actual: undefined
          },

          operatingExpenses: {
            estimated: operatingExpenses,
            actual: undefined
          },

          netOperatingIncome: netOperatingIncome,

          debtService: debtService,

          endOfYearTotals: {
            estimated: endOfYearTotals,
            actual: undefined
          },

          cashOnCash: cashOnCash,

          sale: {
            estimated: sale,
            actual: undefined
          },

          runningTotals: runningTotals,

          variables: investmentVariables
        };

        // replace existing if recalculating
        const ndxExisting = years.findIndex(iy => iy.year == y);
        if (ndxExisting >= 0)
          years[ndxExisting] = investmentYear;
        else
          years.push(investmentYear);
      }
    }

    return years;
  }
}

/**
 * 
 */
export interface IPropertyInfo {
  name: string;
  address?: string;
  description?: string;

  purchasePrice?: number;
  purchaseDate?: string;
  purchaseCosts?: number;

  photoFileEntryList: IDataServiceEntityFileEntryList;

  initialValues: {
    marketValue?: number;

    monthlyRevenue?: number;
    monthlyExpenses: {
      hoa: number;
      grounds: number;
      utilities: number;
      other: number;    
    }

    yearlyExpenses: {
      taxes: number;
      insurance: number;    
    };
  };
}

/**
 * 
 */
export interface IPropertyEntity extends IDataServiceEntity {
  propertyInfo: IPropertyInfo;

  loan: ILoan;

  initialInvestment: number;
  investmentVariables: IInvestmentVariables;

  investmentYears: IInvestmentYear[];

  inactive?: boolean;
}

/**
 * 
 */
export interface IPropertyYear {
  year: number;

  gsi: number;
  expenses: number;

  loanPayment: number;
  taxes: number;
  insurance: number;

  value: number;
  equity: number;

  tenantPaydown: number;
  appreciation: number;
  depreciation: number;
  cashflow: number;
}

/**
 * 
 */
export interface ILoan {
  loanParams: ILoanParams;

  startDate: Date;

  price: number;
  downpayment: number;

  months: number;

  amount: number;

  monthlyPayment: number;
  monthlyRate: number;

  payments: ILoanPayment[];
}

/**
 * 
 */
export interface ILoanParams {
  years: number;
  rate: number;
  closingCosts: number;
  percentDown: number;
}

export interface ILoanPayment {
  monthNumber: number;
  yearNumber: number;

  paymentDate: Date;

  amountRemaining: number;

  principal: number;
  interest: number;

  principalToDate: number;
  interestToDate: number;
}

/**
 * 
 */
export interface IInvestmentYear {
  year: number;
  calendarYear: number;

  grossScheduledIncome: {
    estimated: IGrossScheduledIncome;
    actual?: IGrossScheduledIncome;
  };

  grossOperatingIncome: {
    estimated: IGrossOperatingIncome;
    actual?: IGrossOperatingIncome;
  };

  operatingExpenses: {
    estimated: IOperatingExpenses;
    actual?: IOperatingExpenses;
  };

  netOperatingIncome: number;

  debtService: number;

  endOfYearTotals: {
    estimated: IEndOfYearTotals;
    actual?: IEndOfYearTotals;
  };

  cashOnCash: ICashOnCash;

  sale: {
    estimated: ISale;
    actual?: ISale;
  };

  runningTotals: IRunningTotals;

  variables: IInvestmentVariables;
}

export interface IInvestmentVariables {
  propertyAppreciation: number;
  rentalAppreciation: number;
  propertyManagement: number;
  maintenance: number;
  vacancy: number;
  taxBracket: number;
  saleCommission: number;
  saleClosingCosts: number;
  expenseInflation: number;
  depreciationYears?: number;
}

export interface IGrossScheduledIncome extends IGrossScheduledIncomeComponents {
  total: number;
}
export interface IGrossScheduledIncomeComponents {
  rental: number;
  other: { code: string, title: String, amount: number }[];
}


export interface IGrossOperatingIncome extends IGrossOperatingIncomeComponents {
  total: number;
}
export interface IGrossOperatingIncomeComponents {
  vacancy: number;
  rental: number;
  other: { code: string, title: String, amount: number }[];
}

export interface IOperatingExpenses {
  fixed: IExpensesFixed;
  variable: IExpensesVariable;
  total: number;
}

export interface IExpensesFixed extends IExpensesFixedComponents {
  total: number;
}
export interface IExpensesFixedBasicComponents {
  taxes: number;
  insurance: number;
  
  hoa?: number;
  grounds?: number;
  utilities?: number;
  other?: number;
}
export interface IExpensesFixedComponents extends IExpensesFixedBasicComponents {
  propertyManagement?: number;
}

export interface IExpensesVariable extends IExpensesVariableComponents {
  total: number;
}
export interface IExpensesVariableComponents {
  maintenance: number;
  other: { code: string, title: String, amount: number }[];
}

export interface IEndOfYearTotals extends IEndOfYearTotalsComponents {
  total: number;
}
export interface IEndOfYearTotalsComponents {
  propertyValue: number;
  cashFlow: number;
  depreciationBase: {
    purchasePrice: number;
    landValue: number;
    purchaseCosts: number;
    rehabCosts: number;
    total: number;
  };
  
  depreciation: {
    actual: number;

    passive: number;
    passiveTaxImpact: number;

    active: number;
    activeTaxImpact: number;
  };

  appreciation: number;
  tenantPaydown: number;
  capex: number;
  equity: number;
}

export interface ICashOnCash extends ICashOnCashComonents {
  total: number;
}
export interface ICashOnCashComonents {
  cashflow: number;
  appreciation: number;
  tenantPaydown: number;

  depreciation: {
    passive: number;
    active: number;
  };
}

export interface IRunningTotals {
  cashflow: number;
  
  depreciation: {
    total: number;

    passive: number;
    passiveTaxImpact: number;

    active: number;
    activeTaxImpact: number;
  }
  
  appreciation: number;
  tenantPaydown: number;

  cashOnCash: ICashOnCash;

  capex: number;

  marketValue: number;
  equity: number;

  investmentReturn: number;
  roi: number;
}

export interface ISale extends ISaleComponents {
  net: number;
}
export interface ISaleComponents {
  price: number,
  costs: {
    commission: number,
    closing: number,
    other: number,
    total: number
  };
}

/**
 * 
 */
export interface IPropertyNewData {
  code: string;

  hasLoan: boolean;
  loanParams: ILoanParams;

  name: string;
  description: string;
  address: string;

  purchaseDate: string;
  purchasePrice: number;
  marketValue: number;

  monthlyRevenue: number;
  monthlyExpenses: {
    hoa: number;
    grounds: number;
    utilities: number;
    other: number;
  };
  yearlyExpenses: {
    taxes: number;
    insurance: number;
  };

  propertyManagement: number;

}